import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Input,
  Select,
  Switch,
  message,
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import {
  DEFAULT_HANDOFF,
  HANDOFF_DESTINATIONS,
} from "./constants";
import { updateProspectBot } from "./prospectService";
import styles from "./style.module.scss";

const WEEKDAYS = [
  { value: "mon", labelKey: "sonic_prospect.handoff_day_mon" },
  { value: "tue", labelKey: "sonic_prospect.handoff_day_tue" },
  { value: "wed", labelKey: "sonic_prospect.handoff_day_wed" },
  { value: "thu", labelKey: "sonic_prospect.handoff_day_thu" },
  { value: "fri", labelKey: "sonic_prospect.handoff_day_fri" },
  { value: "sat", labelKey: "sonic_prospect.handoff_day_sat" },
  { value: "sun", labelKey: "sonic_prospect.handoff_day_sun" },
];

function cloneHandoff(raw) {
  const base = raw || DEFAULT_HANDOFF;
  return {
    enabled: !!base.enabled,
    availability: { ...DEFAULT_HANDOFF.availability, ...(base.availability || {}) },
    triggers: { ...DEFAULT_HANDOFF.triggers, ...(base.triggers || {}) },
    qualification: {
      ...DEFAULT_HANDOFF.qualification,
      ...(base.qualification || {}),
    },
    routing: { ...DEFAULT_HANDOFF.routing, ...(base.routing || {}) },
    cannedReplies: Array.isArray(base.cannedReplies)
      ? base.cannedReplies.map((r) => ({
          id: r.id || `r${Math.random().toString(36).slice(2, 8)}`,
          title: r.title || "",
          body: r.body || "",
        }))
      : DEFAULT_HANDOFF.cannedReplies.map((r) => ({ ...r })),
  };
}

/**
 * Handoff & Routing settings (config only — live handoff runtime is a later slice).
 */
const BotHandoffSettings = ({ bot, onBack, onSaved, onOpenLive, embedded = false }) => {
  const { t } = useTranslation();
  const [handoff, setHandoff] = useState(() => cloneHandoff(bot?.handoff));
  const [saving, setSaving] = useState(false);

  const dayOptions = useMemo(
    () => WEEKDAYS.map((d) => ({ value: d.value, label: t(d.labelKey) })),
    [t]
  );

  const destOptions = useMemo(
    () =>
      HANDOFF_DESTINATIONS.map((d) => ({
        value: d.value,
        label: t(d.labelKey),
      })),
    [t]
  );

  const patch = (partial) => setHandoff((prev) => ({ ...prev, ...partial }));
  const patchAvail = (partial) =>
    setHandoff((prev) => ({
      ...prev,
      availability: { ...prev.availability, ...partial },
    }));
  const patchTriggers = (partial) =>
    setHandoff((prev) => ({
      ...prev,
      triggers: { ...prev.triggers, ...partial },
    }));
  const patchQual = (partial) =>
    setHandoff((prev) => ({
      ...prev,
      qualification: { ...prev.qualification, ...partial },
    }));
  const patchRouting = (partial) =>
    setHandoff((prev) => ({
      ...prev,
      routing: { ...prev.routing, ...partial },
    }));

  const addCannedReply = () => {
    setHandoff((prev) => {
      if ((prev.cannedReplies || []).length >= 10) return prev;
      return {
        ...prev,
        cannedReplies: [
          ...(prev.cannedReplies || []),
          {
            id: `r${Date.now().toString(36)}`,
            title: "",
            body: "",
          },
        ],
      };
    });
  };

  const patchCannedReply = (index, partial) => {
    setHandoff((prev) => {
      const list = [...(prev.cannedReplies || [])];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...partial };
      return { ...prev, cannedReplies: list };
    });
  };

  const removeCannedReply = (index) => {
    setHandoff((prev) => ({
      ...prev,
      cannedReplies: (prev.cannedReplies || []).filter((_, i) => i !== index),
    }));
  };
  const handleSave = async () => {
    if (!bot?.id) return;
    setSaving(true);
    try {
      const next = {
        ...bot,
        handoff,
        goals: { ...(bot.goals || {}), handoff: !!handoff.enabled },
      };
      const saved = await updateProspectBot(bot.id, next);
      message.success(t("sonic_prospect.handoff_saved"));
      if (onSaved) onSaved(saved);
    } catch (_) {
      message.error(t("sonic_prospect.msg_save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.handoffPage}>
      <header className={styles.pageHeader}>
        <div>
          {embedded ? null : (
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
          >
            {t("sonic_prospect.btn_back")}
          </Button>
          )}
          <h1>{t("sonic_prospect.handoff_title")}</h1>
          <p>{bot?.name || t("sonic_prospect.preview_bot_fallback")}</p>
        </div>
        <div className={styles.headerActions}>
          {onOpenLive ? (
            <Button onClick={onOpenLive}>
              {t("sonic_prospect.handoff_open_live")}
            </Button>
          ) : null}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {t("sonic_prospect.handoff_btn_save")}
          </Button>
        </div>
      </header>

      <div className={styles.handoffSections}>
        <section className={styles.handoffCard}>
          <div className={styles.handoffCardHead}>
            <div>
              <h2>{t("sonic_prospect.handoff_enable_title")}</h2>
              <p>{t("sonic_prospect.handoff_enable_desc")}</p>
            </div>
            <Switch
              checked={handoff.enabled}
              onChange={(checked) => patch({ enabled: checked })}
            />
          </div>
        </section>

        <section
          className={`${styles.handoffCard} ${
            !handoff.enabled ? styles.handoffCardDisabled : ""
          }`}
        >
          <h2>{t("sonic_prospect.handoff_avail_title")}</h2>
          <p className={styles.handoffHint}>
            {t("sonic_prospect.handoff_avail_desc")}
          </p>
          <div className={styles.handoffRow}>
            <span>{t("sonic_prospect.handoff_avail_toggle")}</span>
            <Switch
              checked={handoff.availability.enabled}
              disabled={!handoff.enabled}
              onChange={(checked) => patchAvail({ enabled: checked })}
            />
          </div>
          <label className={styles.fieldLabel}>
            {t("sonic_prospect.handoff_avail_days")}
          </label>
          <Select
            mode="multiple"
            className={styles.handoffSelect}
            options={dayOptions}
            value={handoff.availability.days}
            disabled={!handoff.enabled}
            onChange={(days) => patchAvail({ days })}
          />
          <div className={styles.handoffTimeRow}>
            <div>
              <label className={styles.fieldLabel}>
                {t("sonic_prospect.handoff_avail_start")}
              </label>
              <Input
                value={handoff.availability.start}
                disabled={!handoff.enabled}
                onChange={(e) => patchAvail({ start: e.target.value })}
                placeholder="09:00"
              />
            </div>
            <div>
              <label className={styles.fieldLabel}>
                {t("sonic_prospect.handoff_avail_end")}
              </label>
              <Input
                value={handoff.availability.end}
                disabled={!handoff.enabled}
                onChange={(e) => patchAvail({ end: e.target.value })}
                placeholder="18:00"
              />
            </div>
          </div>
        </section>

        <section
          className={`${styles.handoffCard} ${
            !handoff.enabled ? styles.handoffCardDisabled : ""
          }`}
        >
          <h2>{t("sonic_prospect.handoff_triggers_title")}</h2>
          <p className={styles.handoffHint}>
            {t("sonic_prospect.handoff_triggers_desc")}
          </p>
          <div className={styles.goalChecks}>
            <Checkbox
              checked={handoff.triggers.highPurchaseIntent}
              disabled={!handoff.enabled}
              onChange={(e) =>
                patchTriggers({ highPurchaseIntent: e.target.checked })
              }
            >
              {t("sonic_prospect.handoff_trigger_intent")}
            </Checkbox>
            <Checkbox
              checked={handoff.triggers.pricingQuestion}
              disabled={!handoff.enabled}
              onChange={(e) =>
                patchTriggers({ pricingQuestion: e.target.checked })
              }
            >
              {t("sonic_prospect.handoff_trigger_pricing")}
            </Checkbox>
            <Checkbox
              checked={handoff.triggers.leadCaptured}
              disabled={!handoff.enabled}
              onChange={(e) =>
                patchTriggers({ leadCaptured: e.target.checked })
              }
            >
              {t("sonic_prospect.handoff_trigger_lead")}
            </Checkbox>
          </div>
        </section>

        <section
          className={`${styles.handoffCard} ${
            !handoff.enabled ? styles.handoffCardDisabled : ""
          }`}
        >
          <h2>{t("sonic_prospect.handoff_qual_title")}</h2>
          <p className={styles.handoffHint}>
            {t("sonic_prospect.handoff_qual_desc")}
          </p>
          <div className={styles.goalChecks}>
            <Checkbox
              checked={handoff.qualification.requireLead}
              disabled={!handoff.enabled}
              onChange={(e) => patchQual({ requireLead: e.target.checked })}
            >
              {t("sonic_prospect.handoff_qual_lead")}
            </Checkbox>
            <Checkbox
              checked={handoff.qualification.requireHighIntent}
              disabled={!handoff.enabled}
              onChange={(e) =>
                patchQual({ requireHighIntent: e.target.checked })
              }
            >
              {t("sonic_prospect.handoff_qual_intent")}
            </Checkbox>
          </div>
        </section>

        <section
          className={`${styles.handoffCard} ${
            !handoff.enabled ? styles.handoffCardDisabled : ""
          }`}
        >
          <h2>{t("sonic_prospect.handoff_routing_title")}</h2>
          <p className={styles.handoffHint}>
            {t("sonic_prospect.handoff_routing_desc")}
          </p>
          <label className={styles.fieldLabel}>
            {t("sonic_prospect.handoff_routing_dest")}
          </label>
          <Select
            className={styles.handoffSelect}
            options={destOptions}
            value={handoff.routing.destination}
            disabled={!handoff.enabled}
            onChange={(destination) => patchRouting({ destination })}
          />
          {handoff.routing.destination === "sales_team" ? (
            <>
              <label className={styles.fieldLabel}>
                {t("sonic_prospect.handoff_routing_team")}
              </label>
              <Input
                value={handoff.routing.teamLabel}
                disabled={!handoff.enabled}
                onChange={(e) => patchRouting({ teamLabel: e.target.value })}
              />
            </>
          ) : null}
          {handoff.routing.destination === "email" ? (
            <>
              <label className={styles.fieldLabel}>
                {t("sonic_prospect.handoff_routing_email")}
              </label>
              <Input
                type="email"
                value={handoff.routing.email}
                disabled={!handoff.enabled}
                onChange={(e) => patchRouting({ email: e.target.value })}
                placeholder="sales@company.com"
              />
            </>
          ) : null}
          {handoff.routing.destination === "hubspot" ? (
            <p className={styles.handoffHint}>
              {t("sonic_prospect.handoff_routing_hubspot_ready")}
            </p>
          ) : null}
        </section>

        <section
          className={`${styles.handoffCard} ${
            !handoff.enabled ? styles.handoffCardDisabled : ""
          }`}
        >
          <h2>{t("sonic_prospect.handoff_canned_title")}</h2>
          <p className={styles.handoffHint}>
            {t("sonic_prospect.handoff_canned_desc")}
          </p>
          {(handoff.cannedReplies || []).map((reply, index) => (
            <div key={reply.id || index} className={styles.handoffCannedRow}>
              <Input
                value={reply.title}
                disabled={!handoff.enabled}
                placeholder={t("sonic_prospect.handoff_canned_title_ph")}
                onChange={(e) =>
                  patchCannedReply(index, { title: e.target.value })
                }
              />
              <Input.TextArea
                rows={2}
                value={reply.body}
                disabled={!handoff.enabled}
                placeholder={t("sonic_prospect.handoff_canned_body_ph")}
                onChange={(e) =>
                  patchCannedReply(index, { body: e.target.value })
                }
              />
              <Button
                danger
                type="link"
                disabled={!handoff.enabled}
                onClick={() => removeCannedReply(index)}
              >
                {t("sonic_prospect.handoff_canned_remove")}
              </Button>
            </div>
          ))}
          <Button
            disabled={!handoff.enabled || (handoff.cannedReplies || []).length >= 10}
            onClick={addCannedReply}
          >
            {t("sonic_prospect.handoff_canned_add")}
          </Button>
        </section>

        <div className={styles.handoffFooter}>
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            {t("sonic_prospect.handoff_btn_save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BotHandoffSettings;
